import React from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITIES } from "@/lib/constants";
import { TASK_TYPES, SORTS, statusOptionsFor } from "@/lib/tasks";

const ALL = "__all";

function Pick({ value, onChange, placeholder, options, testid, className = "w-[150px]" }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className={`h-9 text-sm ${className}`} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function TaskFilters({ f, set, categories, projects, floors, members, team }) {
  const statusOpts = [...statusOptionsFor("ticket"), ...statusOptionsFor("defect")].filter((o, i, a) => a.findIndex((x) => x.value === o.value) === i);
  const hasExtra = f.q || f.project_id || f.floor || f.priority || f.status || f.type || f.assignee_id || f.date_from || f.date_to;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-3" data-testid="task-filters">
      <div className="flex flex-wrap gap-1.5" data-testid="task-category-chips">
        {categories.map((c) => (
          <button key={c.value} onClick={() => set({ category: c.value })} data-testid={`task-cat-${c.value}`}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${f.category === c.value ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {c.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Titel, Nummer, Gebäude, Raum …" className="h-9 pl-8" data-testid="task-search-input" />
        </div>
        <Pick value={f.project_id} onChange={(v) => set({ project_id: v })} placeholder="Alle Objekte" testid="task-filter-project" className="w-[170px]"
          options={projects.map((p) => ({ value: p.id, label: p.name }))} />
        <Pick value={f.floor} onChange={(v) => set({ floor: v })} placeholder="Alle Etagen" testid="task-filter-floor" options={floors.map((x) => ({ value: x, label: x }))} />
        <Pick value={f.priority} onChange={(v) => set({ priority: v })} placeholder="Alle Prioritäten" testid="task-filter-priority" options={[...PRIORITIES].reverse()} />
        <Pick value={f.status} onChange={(v) => set({ status: v })} placeholder="Alle Status" testid="task-filter-status" options={statusOpts} />
        <Pick value={f.type} onChange={(v) => set({ type: v })} placeholder="Alle Typen" testid="task-filter-type"
          options={Object.entries(TASK_TYPES).map(([value, label]) => ({ value, label }))} />
        {team && <Pick value={f.assignee_id} onChange={(v) => set({ assignee_id: v })} placeholder="Alle Mitarbeiter" testid="task-filter-assignee" className="w-[170px]"
          options={[{ value: "__none", label: "— Nicht zugewiesen —" }, ...members.map((m) => ({ value: m.user_id, label: m.name }))]} />}
      </div>
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <span className="text-xs text-slate-500">Fällig von</span>
        <Input type="date" value={f.date_from} onChange={(e) => set({ date_from: e.target.value })} className="h-9 w-[150px]" data-testid="task-filter-date-from" />
        <span className="text-xs text-slate-500">bis</span>
        <Input type="date" value={f.date_to} onChange={(e) => set({ date_to: e.target.value })} className="h-9 w-[150px]" data-testid="task-filter-date-to" />
        {f.category === "done" && (
          <Pick value={String(f.done_days)} onChange={(v) => set({ done_days: Number(v || 30) })} placeholder="Zeitraum" testid="task-filter-done-days" className="w-[150px]"
            options={[{ value: "7", label: "Letzte 7 Tage" }, { value: "30", label: "Letzte 30 Tage" }, { value: "90", label: "Letzte 90 Tage" }, { value: "365", label: "Letztes Jahr" }]} />
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">Sortieren</span>
          <Select value={f.sort} onValueChange={(v) => set({ sort: v })}>
            <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="task-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          {hasExtra && (
            <button onClick={() => set({ q: "", project_id: "", floor: "", priority: "", status: "", type: "", assignee_id: "", date_from: "", date_to: "" })}
              className="text-xs text-primary font-medium flex items-center gap-1" data-testid="task-filter-reset"><X className="h-3.5 w-3.5" />Filter zurücksetzen</button>
          )}
        </div>
      </div>
    </div>
  );
}
